// src/app/interceptors/error.interceptor.ts
import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { NotificationService } from '../core';

// Optional: set this header on requests you DON'T want auto-notified
const SKIP_HEADER = 'X-Skip-Error-Notify';

export const responseInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);

  // pass through, but mark if caller asked to skip notifications
  const skipNotify = req.headers.has(SKIP_HEADER);
  const cleanReq = skipNotify ? req.clone({ headers: req.headers.delete(SKIP_HEADER) }) : req;

  return next(cleanReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (!skipNotify) {
        const message = 'Server Error: ' + buildUserMessage(err);
        notificationService.showNotification({
          type: 'error',
          message,
          timeout: 9000
        });

      }
      return throwError(() => err); // rethrow so callers can handle if needed
    })
  );
};

/** Map HttpErrorResponse -> friendly message (tweak to your backend shape) */
function buildUserMessage(error: HttpErrorResponse): string {
  // Network / CORS / offline
  if (error.status === 0) {
    return 'Network error — check your internet connection and try again.';
  }

  // If backend returns a structured payload
  const payload = error.error;
  const backendMsg =
    (typeof payload === 'string' && payload) ||
    payload?.message ||
    payload?.error ||
    payload?.title;

  if (backendMsg) return backendMsg;

  // Fallback by status
  switch (error.status) {
    case 400: return 'Bad request. Please review your input.';
    case 401: return 'Unauthorized. Please sign in again.';
    case 403: return 'Forbidden. You do not have permission.';
    case 404: return 'Resource not found.';
    case 409: return 'Conflict. This record may already exist.';
    case 422: return 'Validation failed. Please check highlighted fields.';
    case 429: return 'Too many requests. Please slow down and try again.';
    case 500: return 'Server error. Please try again later.';
    case 503: return 'Service unavailable. Please try again shortly.';
    default:  return `Error ${error.status}: ${error.statusText || 'Request failed'}`;
  }
}
